import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { wkt } from '../usawkt.js';
import areasIcon from '../assets/icons/areas-stroke.png';
import moment from 'moment-timezone';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

const AreasContext = createContext();

export const AreasContextProvider = ({ children }) => {
    const [area, setArea] = useState({
        area_id: 1751,
        user_id: -1,
        area_name: "United States",
        area_description: "",
        geom: "0103000020E61000000100000004000000149598D04F5A59C07CEC6BB65F844340A08CA4F5CE7159C00C65EC0DE6D24240C4563D93A71B59C06605A2D4C0D44240149598D04F5A59C07CEC6BB65F844340",
        area_size_meters_squared: 0,
        created_timestamp: "2024-08-22 17:55:04.802192",
        zoom: 4,
        is_default: false,
        active: false,
        wkt: wkt,
        bounds: '-125.0,24.396308,-66.93457,49.384358'
    });

    const [modal, setModal] = useState({
        type: false,
        content: null
    });

    const [myAreas, setMyAreas] = useState(null);
    const [teamAreas, setTeamAreas] = useState(null);

    const WKTtoRings = (wkt) => {
        const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
            return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
        });
        return rings;
    };

    const getCentroid = (rings) => {
        let first = rings[0], last = rings[rings.length-1];
 
        if (first[0] != last[0] || first[1] != last[1])
            rings.push(first);

        let twicearea = 0, x=0, y=0, nrings = rings.length, p1, p2, f;
 
        for ( let i = 0, j = nrings-1 ; i < nrings ; j=i++ ) {
           p1 = rings[i]; p2 = rings[j];
           f = p1[0]*p2[1] - p2[0]*p1[1];
           twicearea += f;          
           x += ( p1[0] + p2[0] ) * f;
           y += ( p1[1] + p2[1] ) * f;
        }
        f = twicearea * 3;
        return [ x/f, y/f ];
    };

    const formatPolicyAndUniqueIDs = (input) => {
      if (input.includes("Policy ID") && input.includes("Unique ID")) {
        return input.split(',')
                    .map(part => part.trim())
                    .join('<br />');  // Use <br /> tags for HTML line breaks
      }
      return input;
    };
  
    const convertTo12HourFormat = (dateTimeString) => {
      const time = moment.tz(dateTimeString, "UTC");
    
      const localTime = time.tz(moment.tz.guess()).format('YYYY-MM-DD h:mm A');
    
      return localTime;
    }
  
    const convertMetersToSquareMiles = (wkt) => {
      const data = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: [WKTtoRings(wkt)]
              }
            }
          ]
      };
  
      const areaInMetersSquared = turf.area(data);
        
      const conversionFactor = 0.000000386102;
      const areaInSquareMiles = areaInMetersSquared * conversionFactor;
      return Math.round(areaInSquareMiles * 100) / 100;
    };

    const plotAreas = (map, areas) => {
        if (!map?.current) {
            console.warn('plotAreas: Map not initialized, skipping plot');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('plotAreas: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                plotAreas(map, areas);
            });
            return;
        }
        const existingLayers = map.current.getStyle().layers?.filter(layer =>
          layer.id.includes('Layer-areas-symbol') || layer.id.includes('Layer-areas-blue')
        );
        if (existingLayers && existingLayers.length > 0) {
          existingLayers.forEach(layer => map.current.removeLayer(layer.id));
        }
        if (map.current.getStyle().sources['Source-areas-symbols']) {
          map.current.removeSource('Source-areas-symbols');
        }
        if (map.current.getStyle().sources['Source-areas-blue-symbols']) {
          map.current.removeSource('Source-areas-blue-symbols');
        }
        if (map.current.hasImage('Image-areas-Icon')) {
          map.current.removeImage('Image-areas-Icon');
        }
      
        map.current.loadImage(areasIcon, (error, image) => {
          if (error) throw error;
          map.current.addImage('Image-areas-Icon', image);
      
          // Separate areas into regular and blue-tinted (proper_insure_new_pif === true)
          const regularAreas = areas.filter(area => !area.proper_insure_new_pif);
          const blueAreas = areas.filter(area => area.proper_insure_new_pif === true);
      
          const createGeoJSON = (areaList) => ({
            type: 'FeatureCollection',
            features: areaList.map(area => {
              let coordinates = [];
              if (area.geometry || area.st_astext) {
                const wkt = area.geometry || area.st_astext;
                const rings = WKTtoRings(wkt);
                const centroid = getCentroid(rings);
                coordinates = centroid;
              } else {
                coordinates = [area.longitude, area.latitude];
              }
              return {
                type: 'Feature',
                properties: area,
                geometry: {
                  type: 'Point',
                  coordinates: coordinates
                }
              };
            })
          });

          const regularGeoJSON = createGeoJSON(regularAreas);
          const blueGeoJSON = createGeoJSON(blueAreas);
    
          // Add source and layer for regular areas
          if (regularAreas.length > 0) {
            map.current.addSource('Source-areas-symbols', {
              type: 'geojson',
              data: regularGeoJSON
            });
        
            map.current.addLayer({
              id: 'Layer-areas-symbol',
              type: 'symbol',
              source: 'Source-areas-symbols',
              minzoom: 9,
              layout: {
                'icon-image': 'Image-areas-Icon',
                'icon-size': 0.60,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
              }
            });
          }

          // Add source and layer for blue-tinted areas (with blue circle background)
          if (blueAreas.length > 0) {
            map.current.addSource('Source-areas-blue-symbols', {
              type: 'geojson',
              data: blueGeoJSON
            });

            // Add blue circle layer behind the icon
            map.current.addLayer({
              id: 'Layer-areas-blue-circle',
              type: 'circle',
              source: 'Source-areas-blue-symbols',
              minzoom: 9,
              paint: {
                'circle-radius': 12,
                'circle-color': '#0066FF',
                'circle-opacity': 0.6,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#0066FF',
                'circle-stroke-opacity': 0.8
              }
            });

            map.current.addLayer({
              id: 'Layer-areas-blue-symbol',
              type: 'symbol',
              source: 'Source-areas-blue-symbols',
              minzoom: 9,
              layout: {
                'icon-image': 'Image-areas-Icon',
                'icon-size': 0.60,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
              }
            });
          }

          // Track current popup to prevent duplicates
          let currentPopup = null;

          // Shared click handler function
          const handleAreaClick = (e, layerId) => {
            // Prevent duplicate popups by removing existing one first
            if (currentPopup) {
              currentPopup.remove();
              currentPopup = null;
            }
            
            const features = map.current.queryRenderedFeatures(e.point, {
              layers: [layerId]
            });
            if (!features.length) return;

            const feature = features[0].properties;

            const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-areas-focused`));

            if (layers.length > 0) {
                layers.forEach((layer) => map.current.removeLayer(layer.id));
            }
    
            if (map.current.getStyle().sources[`Source-areas-focused-${feature.id}`]) {
                map.current.removeSource(`Source-areas-focused-${feature.id}`);
            }

            const rings = feature.geometry ? WKTtoRings(feature.geometry) : '';
        
            const location = [feature.longitude, feature.latitude];
            // map.current.flyTo({ center: location, zoom: 15, pitch: 0, offset: [250, 0] });

            currentPopup = new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
            .setLngLat(location)
            .setHTML(`
              <div>
                <div class='alert-popup-title'>${feature.name}</div>
                <div class='alert-popup-row'>
                  <div class='alert-popup-row-name'>Description:</div>
                  <div class='alert-popup-row-value'>${feature.description ? formatPolicyAndUniqueIDs(feature.description) : 'No Description'}</div>
                </div>
                <div class='alert-popup-row'>
                  <div class='alert-popup-row-name'>Type:</div>
                  <div class='alert-popup-row-value'>${feature.type === 'point' ? 'POINT/ADDRESS' : 'POLYGON'}</div>
                </div>
                ${feature.type === 'polygon'
                  && `<div class='alert-popup-row'>
                      <div class='alert-popup-row-name'>Area:</div>
                      <div class='alert-popup-row-value'>
                        ${convertMetersToSquareMiles(feature.geometry)} square miles
                      </div>
                    </div>`}
                ${feature.value ?
                   `<div class='alert-popup-row'>
                      <div class='alert-popup-row-name'>Total Asset Value:</div>
                      <div class='alert-popup-row-value'>
                        $${Number(feature.value).toLocaleString()}
                      </div>
                    </div>` : ' '}
                ${feature.proper_insure_new_pif === true
                  ? `<div class='alert-popup-row'>
                      <div class='alert-popup-row-name'>Policy Updated:</div>
                      <div class='alert-popup-row-value'>
                        The policy has updated as of ${convertTo12HourFormat(feature.created_timestamp)}
                      </div>
                    </div>`
                  : ''
                }
                <div class='alert-popup-row'>
                  <div class='alert-popup-row-name'>Created:</div>
                  <div class='alert-popup-row-value'>${convertTo12HourFormat(feature.created_timestamp)}</div>
                </div>
              </div>
            `)
            .addTo(map.current);
          };
      
          // Add click handlers for symbol layers only (circle layer is visual only)
          if (regularAreas.length > 0) {
            map.current.on('click', 'Layer-areas-symbol', (e) => handleAreaClick(e, 'Layer-areas-symbol'));
          }
          if (blueAreas.length > 0) {
            map.current.on('click', 'Layer-areas-blue-symbol', (e) => handleAreaClick(e, 'Layer-areas-blue-symbol'));
          }
      
          // Change the cursor to a pointer when over the layers
          if (regularAreas.length > 0) {
            map.current.on('mouseenter', 'Layer-areas-symbol', () => {
              map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'Layer-areas-symbol', () => {
              map.current.getCanvas().style.cursor = '';
            });
          }
          if (blueAreas.length > 0) {
            map.current.on('mouseenter', 'Layer-areas-blue-symbol', () => {
              map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'Layer-areas-blue-symbol', () => {
              map.current.getCanvas().style.cursor = '';
            });
            map.current.on('mouseenter', 'Layer-areas-blue-circle', () => {
              map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'Layer-areas-blue-circle', () => {
              map.current.getCanvas().style.cursor = '';
            });
          }
        });
      };

    const getAreas = (map, token) => {
        axios.get(`https://firewatch-api.space-eyes.com/locations?page=1`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        .then((response) => {
            let tempAreas = [...response.data.areas];
            const totalPages = response.data.pagination.totalPages;
            
            const promises = [];
            for (let page = 2; page <= totalPages; page++) {
                promises.push(
                    axios.get(`https://firewatch-api.space-eyes.com/locations?page=${page}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                );
            }
            
            Promise.all(promises)
                .then((responses) => {
                    responses.forEach((resp) => {
                        tempAreas.push(...resp.data.areas);
                    });
                    
                    setMyAreas(tempAreas.filter((a) => a.user_id));
                    setTeamAreas(tempAreas.filter((a) => !a.user_id));
                    console.log(tempAreas[0]);
                    plotAreas(map, tempAreas);
                })
                .catch((error) => {
                    console.log('Error fetching subsequent pages:', error);
                    setMyAreas([]);
                    setTeamAreas([])
                });
        })
        .catch((error) => {
            console.log('Error fetching page 1:', error);
            setMyAreas([]);
            setTeamAreas([])
        });
    };
  
    const value = {
        area,
        setArea,
        myAreas,
        setMyAreas,
        teamAreas,
        plotAreas,
        setTeamAreas,
        getAreas,
        modal,
        setModal
     };
  
    return (
        <AreasContext.Provider 
            value={value}
        >
        {children}
        </AreasContext.Provider>
    );
};

export const useAreasContext = () => useContext(AreasContext);
